import { Polyline, Postprocessing } from '@editor/services/PolylineExtraction/interfaces'

import { dot, magnitude, polylineLength } from '../../utils/math'

export interface SmoothingConfig {
  /**
   * Type of smoothing to apply
   */
  method?: 'movingAverage' | 'gaussian'

  /**
   * Window size for smoothing
   */
  windowSize?: number

  /**
   * Gaussian sigma (for gaussian method only)
   */
  sigma?: number

  /**
   * Number of smoothing passes
   */
  passes?: number

  /**
   * Minimum polyline length to process
   */
  minLength?: number

  /**
   * Maximum allowed deviation from original points
   */
  maxDeviation?: number

  /**
   * Preserve endpoints during smoothing
   */
  preserveEndpoints?: boolean

  /**
   * Maximum allowed turn angle in degrees.
   */
  maxTurnAngle?: number
}

/**
 * Advanced smoothing for polylines with multiple methods and multi-pass capability
 */
export class Smoothing extends Postprocessing {
  protected config: Required<SmoothingConfig>

  constructor(config?: SmoothingConfig) {
    super()
    this.config = {
      method: 'gaussian',
      windowSize: 7,
      passes: 2,
      sigma: 1.0,
      minLength: 0.01,
      maxDeviation: 0.005,
      preserveEndpoints: true,
      maxTurnAngle: 180,
      ...config,
    }
    this.validateConfig()
  }

  getName(): string {
    const { method, passes, windowSize } = this.config
    return `Smoothing(${method}, w=${windowSize}, passes=${passes})`
  }

  run(polylines: Polyline[]): Polyline[] {
    if (polylines.length === 0) return []

    this.logger?.log(
      `    🔧 Smoothing: ${polylines.length} polylines, method=${this.config.method}, passes=${this.config.passes}`,
    )

    const smoothed = polylines.map((polyline) => {
      const length = polylineLength(polyline)

      if (length < (this.config.minLength || 0.01)) {
        return polyline
      }

      return this.smoothPolyline(polyline)
    })

    this.logger?.log(`    ✅ Smoothing complete`)
    return smoothed
  }

  private smoothPolyline(polyline: Polyline): Polyline {
    if (polyline.length < 3) return polyline

    let current = polyline.slice() // Make a copy

    // Apply multiple passes
    for (let pass = 0; pass < (this.config.passes || 1); pass += 1) {
      switch (this.config.method) {
        case 'movingAverage':
          current = this.applyMovingAverage(current)
          break
        case 'gaussian':
          current = this.applyGaussianSmoothing(current)
          break
        default:
          throw new Error(`Unknown smoothing method: ${String(this.config.method)}`)
      }
    }

    return this.removeSharpTurns(current)
  }

  private applyMovingAverage(polyline: Polyline): Polyline {
    const { windowSize } = this.config
    if (polyline.length <= windowSize) {
      return polyline // Not enough points to smooth
    }

    const halfWindow = Math.floor(windowSize / 2)
    const result: Polyline = []

    for (let i = 0; i < polyline.length; i += 1) {
      // Apply moving average with adaptive window for boundary points
      const actualHalfWindow = Math.min(halfWindow, i, polyline.length - 1 - i)
      const startIdx = i - actualHalfWindow
      const endIdx = i + actualHalfWindow

      let sumX = 0
      let sumY = 0
      let sumZ = 0
      let count = 0

      for (let j = startIdx; j <= endIdx; j += 1) {
        sumX += polyline[j].x
        sumY += polyline[j].y
        sumZ += polyline[j].z
        count += 1
      }

      result.push({
        x: sumX / count,
        y: sumY / count,
        z: sumZ / count,
      })
    }

    return result
  }

  private applyGaussianSmoothing(polyline: Polyline): Polyline {
    const { windowSize, sigma = 1.0 } = this.config
    if (polyline.length <= windowSize) {
      return polyline
    }

    const halfWindow = Math.floor(windowSize / 2)
    const result: Polyline = []

    // Pre-calculate Gaussian weights
    const weights: number[] = []
    for (let i = -halfWindow; i <= halfWindow; i += 1) {
      weights.push(Math.exp(-(i * i) / (2 * sigma * sigma)))
    }

    for (let i = 0; i < polyline.length; i += 1) {
      const actualHalfWindow = Math.min(halfWindow, i, polyline.length - 1 - i)
      const startIdx = i - actualHalfWindow
      const endIdx = i + actualHalfWindow

      let sumX = 0
      let sumY = 0
      let sumZ = 0
      let totalWeight = 0

      for (let j = startIdx; j <= endIdx; j += 1) {
        const weightIdx = j - i + halfWindow
        const weight = weightIdx >= 0 && weightIdx < weights.length ? weights[weightIdx] : 0
        sumX += polyline[j].x * weight
        sumY += polyline[j].y * weight
        sumZ += polyline[j].z * weight
        totalWeight += weight
      }

      if (totalWeight > 0) {
        result.push({
          x: sumX / totalWeight,
          y: sumY / totalWeight,
          z: sumZ / totalWeight,
        })
      } else {
        result.push(polyline[i])
      }
    }

    return result
  }

  private removeSharpTurns(polyline: Polyline): Polyline {
    const { maxTurnAngle } = this.config
    if (polyline.length < 3) {
      return polyline
    }

    const result: Polyline = []
    const maxTurnRadians = (maxTurnAngle * Math.PI) / 180

    // Process all points including endpoints, but be more lenient with endpoints
    for (let i = 0; i < polyline.length; i += 1) {
      if (i === 0 || i === polyline.length - 1) {
        // Always keep actual endpoints, but they may have been smoothed by moving average
        result.push(polyline[i])
      } else {
        // Check middle points for sharp turns
        const prev = polyline[i - 1]
        const curr = polyline[i]
        const next = polyline[i + 1]

        // Calculate vectors
        const v1 = {
          x: curr.x - prev.x,
          y: curr.y - prev.y,
          z: curr.z - prev.z,
        }
        const v2 = {
          x: next.x - curr.x,
          y: next.y - curr.y,
          z: next.z - curr.z,
        }

        // Calculate angle between vectors
        const dotProduct = dot(v1, v2)
        const mag1 = magnitude(v1)
        const mag2 = magnitude(v2)

        if (mag1 > 0 && mag2 > 0) {
          const cosAngle = Math.max(-1, Math.min(1, dotProduct / (mag1 * mag2)))
          const angle = Math.acos(cosAngle)
          const turnAngle = Math.PI - angle // Turn angle is supplement of direction angle

          // Keep point if turn angle is within acceptable range
          if (turnAngle <= maxTurnRadians) {
            result.push(curr)
          }
          // else: skip this point as it creates too sharp a turn
        } else {
          // Keep point if we can't calculate angle (zero-length vectors)
          result.push(curr)
        }
      }
    }

    return result
  }

  private validateConfig(): void {
    if (this.config.windowSize < 3) {
      throw new Error('[Smoothing] windowSize must be at least 3')
    }
    if (this.config.windowSize % 2 === 0) {
      throw new Error('[Smoothing] windowSize must be odd')
    }
    if (this.config.passes && this.config.passes < 1) {
      throw new Error('[Smoothing] passes must be at least 1')
    }
    if (this.config.sigma && this.config.sigma <= 0) {
      throw new Error('[Smoothing] sigma must be positive')
    }
  }
}
